import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { useAuthState, useClearAuthData } from '~/auth';
import {
  AccountChangeCountryRequest,
  AccountChangeCountryResponse,
  AccountChangeDescriptionRequest,
  AccountChangeDescriptionResponse,
  AccountChangeUserNameRequest,
  AccountChangeUserNameResponse,
  AccountSyncEmailRequest,
  AccountSyncEmailResponse,
  UserInternal,
} from '~/server/src/dtos';

import { getFn, postFn, putFn, queryClient, RequestError } from './api';
import { useFirebaseAnalytics } from './useFirebase';

export const account = createQueryKeys('account', {
  detail: null,
  profilePicture: null,
});

export const getAccount = `/account`;
export const getAccountProfilePicture = `/account/profile-picture`;
export const putAccountUsername = `${getAccount}/username`;
export const putAccountDescription = `${getAccount}/description`;
export const putAccountCountry = `${getAccount}/country`;
export const postAccountProfilePicture = `${getAccount}/profile-picture`;
export const postAccountSyncEmail = `${getAccount}/sync-email`;

const accountDetailQueryOptions = () => ({
  ...account.detail,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getFn<UserInternal>(getAccount, undefined, undefined, { signal }),
});

export const useAccount = (
  options?: Omit<
    Parameters<typeof useQuery<UserInternal, RequestError>>[0],
    'queryKey' | 'queryFn'
  >,
) => {
  const { isAuthorized, isAuthSettled, state: authState } = useAuthState();
  const clearAuthData = useClearAuthData();

  const query = useQuery<UserInternal, RequestError>({
    ...accountDetailQueryOptions(),
    ...options,
    // Wait for auth settled so startup refresh runs before we trust account 404 (avoids clearing on reload before refresh can recover).
    enabled:
      authState !== 'initializing' && isAuthSettled && isAuthorized && (options?.enabled ?? true),
  });

  // Account 404 means session is unusable; use full cleanup so authStorage, transport auth token, query cache, and controller stay in sync.
  useEffect(() => {
    if (query.error && 'status' in query.error && query.error.status === 404) {
      clearAuthData();
    }
  }, [query.error, clearAuthData]);

  return useMemo(
    () => ({ ...query, isLoading: authState === 'initializing' || query.isLoading }),
    [authState, query],
  );
};

export const prefetchAccount = async () => {
  return await queryClient.prefetchQuery({
    ...accountDetailQueryOptions(),
  });
};

export const useChangeUsername = (options?: {
  onSuccess?: (data: AccountChangeUserNameResponse) => void;
}) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<AccountChangeUserNameResponse, RequestError, AccountChangeUserNameRequest>({
    mutationFn: (data: AccountChangeUserNameRequest) => putFn(putAccountUsername, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: account._def });
      analytics?.logEvent('update_username');
      options?.onSuccess?.(data);
    },
  });
};

export const useChangeDescription = (options?: {
  onSuccess?: (data: AccountChangeDescriptionResponse) => void;
}) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<
    AccountChangeDescriptionResponse,
    RequestError,
    AccountChangeDescriptionRequest
  >({
    mutationFn: (data: AccountChangeDescriptionRequest) => putFn(putAccountDescription, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: account._def });
      analytics?.logEvent('update_description');
      options?.onSuccess?.(data);
    },
  });
};

export const useChangeCountry = (options?: {
  onSuccess?: (data: AccountChangeCountryResponse) => void;
}) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<AccountChangeCountryResponse, RequestError, AccountChangeCountryRequest>({
    mutationFn: (data: AccountChangeCountryRequest) => putFn(putAccountCountry, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: account._def });
      analytics?.logEvent('update_country');
      options?.onSuccess?.(data);
    },
  });
};

export const useChangeProfilePicture = () => {
  return useMutation<{ profilePicture: string; edited: boolean }, RequestError, File>({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('profilePicture', file);
      return postFn<{ profilePicture: string; edited: boolean }>(
        postAccountProfilePicture,
        formData,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: account._def });
    },
  });
};

export const useSyncEmail = (options?: {
  onSuccess?: (data: AccountSyncEmailResponse) => void;
}) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<AccountSyncEmailResponse, RequestError, AccountSyncEmailRequest>({
    mutationFn: (data: AccountSyncEmailRequest) => postFn(postAccountSyncEmail, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: account._def });
      analytics?.logEvent('sync_email');
      options?.onSuccess?.(data);
    },
  });
};
